import type { LrcDocument } from '../lyrics/lrc';
import { activeIndex, isTimed, lineProgress } from '../lyrics/lrc';
import type { MusicBands } from '../audio/MusicEngine';

export type LyricsDrawOpts = {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  /** Playback / export audio clock (seconds). */
  audioTime: number;
  doc: LrcDocument | null;
  /** Extra user offset seconds (plus LRC tag offset). */
  userOffsetSec: number;
  bands: MusicBands;
  /** karaoke = big center stack; hud = compact lower third */
  mode: 'karaoke' | 'hud' | 'off';
};

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const trial = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(trial).width > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = trial;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

/**
 * Draw synced lyrics over the current frame.
 * Call after the visualizer so text sits on top.
 */
export function drawLyricsOverlay(opts: LyricsDrawOpts): void {
  const { ctx, w, h, audioTime, doc, userOffsetSec, bands, mode } = opts;
  if (mode === 'off' || !doc || !doc.lines.length) return;

  const tagOff = (doc.meta.tagOffsetMs || 0) / 1000;
  const offsetSec = userOffsetSec + tagOff;

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (!isTimed(doc)) {
    // Static plain lyrics block (lower third)
    const fontSize = Math.max(16, Math.round(w * 0.022));
    ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
    const maxW = w * 0.78;
    const plain = doc.lines.map((l) => l.text).join(' · ');
    const wrapped = wrapText(ctx, plain.slice(0, 220), maxW);
    const y0 = h * 0.72;
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    const boxH = wrapped.length * (fontSize * 1.35) + 24;
    roundFill(ctx, w * 0.1, y0 - boxH / 2, w * 0.8, boxH, 12);
    ctx.fillStyle = 'rgba(232,238,245,0.82)';
    wrapped.forEach((line, i) => {
      ctx.fillText(line, w / 2, y0 - ((wrapped.length - 1) * fontSize * 0.6) / 2 + i * fontSize * 1.3);
    });
    ctx.restore();
    return;
  }

  const idx = activeIndex(doc.lines, audioTime, offsetSec);
  const progress = lineProgress(doc.lines, idx, audioTime, offsetSec);
  const pulse = 0.04 * bands.energy + 0.06 * bands.beat;

  if (mode === 'karaoke') {
    drawKaraoke(ctx, w, h, doc.lines, idx, progress, pulse);
  } else {
    drawHud(ctx, w, h, doc.lines, idx, progress, pulse);
  }

  ctx.restore();
}

function drawKaraoke(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  lines: { time: number; text: string }[],
  idx: number,
  progress: number,
  pulse: number,
): void {
  const maxW = w * 0.86;
  const cy = h * 0.58;

  // soft panel
  const panelH = h * 0.34;
  const g = ctx.createLinearGradient(0, cy - panelH / 2, 0, cy + panelH / 2);
  g.addColorStop(0, 'rgba(4, 10, 16, 0)');
  g.addColorStop(0.25, 'rgba(4, 10, 16, 0.55)');
  g.addColorStop(0.75, 'rgba(4, 10, 16, 0.55)');
  g.addColorStop(1, 'rgba(4, 10, 16, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, cy - panelH / 2, w, panelH);

  const slots: { i: number; weight: number; y: number }[] = [];
  for (let d = -2; d <= 2; d++) {
    const i = idx + d;
    if (i < 0 || i >= lines.length) continue;
    if (idx < 0 && d !== 1) continue;
    slots.push({
      i,
      weight: d === 0 ? 1 : d === -1 || d === 1 ? 0.55 : 0.28,
      y: cy + d * Math.max(36, h * 0.055),
    });
  }

  // before first line: show upcoming
  if (idx < 0 && lines[0]) {
    slots.length = 0;
    slots.push({ i: 0, weight: 0.7, y: cy });
  }

  for (const s of slots) {
    const active = s.i === idx;
    const base = Math.max(18, Math.round(w * (active ? 0.038 : 0.024)));
    const size = base * (active ? 1 + pulse : 1);
    ctx.font = `${active ? 700 : 500} ${size}px ui-sans-serif, system-ui, sans-serif`;
    const wrapped = wrapText(ctx, lines[s.i].text, maxW);
    const alpha = active ? 0.92 : 0.35 * s.weight;
    ctx.fillStyle = active
      ? `rgba(255,255,255,${alpha})`
      : `rgba(180,210,220,${alpha})`;
    if (active) {
      ctx.shadowColor = `rgba(86,240,200,${0.35 + pulse})`;
      ctx.shadowBlur = 18 + progress * 8;
    } else {
      ctx.shadowBlur = 0;
    }
    const blockH = wrapped.length * size * 1.2;
    wrapped.forEach((line, li) => {
      ctx.fillText(line, w / 2, s.y - blockH / 2 + size / 2 + li * size * 1.2);
    });
    ctx.shadowBlur = 0;

    // progress underline on active
    if (active && wrapped.length) {
      const tw = Math.min(
        maxW,
        Math.max(...wrapped.map((l) => ctx.measureText(l).width)),
      );
      const uw = tw * progress;
      ctx.fillStyle = `rgba(86,240,200,${0.55 + pulse})`;
      ctx.fillRect(w / 2 - tw / 2, s.y + blockH / 2 + 8, uw, 3);
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(w / 2 - tw / 2, s.y + blockH / 2 + 8, tw, 3);
    }
  }
}

function drawHud(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  lines: { time: number; text: string }[],
  idx: number,
  progress: number,
  pulse: number,
): void {
  const i = idx < 0 ? 0 : idx;
  if (!lines[i]) return;
  const fontSize = Math.max(16, Math.round(w * 0.028));
  ctx.font = `700 ${fontSize}px ui-sans-serif, system-ui, sans-serif`;
  const maxW = w * 0.8;
  const wrapped = wrapText(ctx, lines[i].text, maxW);
  const pad = Math.max(16, w * 0.02);
  const blockH = wrapped.length * fontSize * 1.25 + 20;
  const y = h - pad - blockH / 2 - 8;

  ctx.fillStyle = `rgba(0,0,0,${0.4 + pulse * 0.2})`;
  roundFill(ctx, w * 0.1, y - blockH / 2, w * 0.8, blockH, 14);

  ctx.fillStyle = idx < 0 ? 'rgba(200,220,230,0.55)' : 'rgba(255,255,255,0.94)';
  ctx.shadowColor = 'rgba(86,240,200,0.35)';
  ctx.shadowBlur = 12;
  wrapped.forEach((line, li) => {
    ctx.fillText(
      line,
      w / 2,
      y - ((wrapped.length - 1) * fontSize * 0.6) / 2 + li * fontSize * 1.25,
    );
  });
  ctx.shadowBlur = 0;

  if (idx >= 0) {
    const tw = Math.min(maxW * 0.9, w * 0.5);
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.fillRect(w / 2 - tw / 2, y + blockH / 2 - 10, tw, 3);
    ctx.fillStyle = `rgba(86,240,200,${0.7 + pulse})`;
    ctx.fillRect(w / 2 - tw / 2, y + blockH / 2 - 10, tw * progress, 3);
  }

  // next line teaser
  if (idx >= 0 && lines[idx + 1]) {
    ctx.font = `500 ${Math.max(12, Math.round(fontSize * 0.65))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(180,200,210,0.45)';
    const next = wrapText(ctx, lines[idx + 1].text, maxW)[0];
    ctx.fillText(next, w / 2, y + blockH / 2 + Math.max(14, fontSize * 0.7));
  }
}

function roundFill(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
  ctx.fill();
}
